import { Fragment, useState } from "react";
import { Icon } from "./Icon";
import type { DashboardNavItem } from "./types";

type SideNavItemProps = {
  item: DashboardNavItem;
  depth?: number;
  onNavigate: (route: string) => void;
};

function SideNavItem({ item, depth = 0, onNavigate }: SideNavItemProps) {
  const [expanded, setExpanded] = useState(item.active || item.children?.some((child) => child.active) || false);
  const visibleChildren = item.children?.filter((child) => child.visible !== false) || [];
  const hasChildren = visibleChildren.length > 0;

  if (item.visible === false) return null;

  const handleClick = () => {
    if (item.disabled) return;
    if (hasChildren) setExpanded((value) => !value);
    item.onSelect?.();
    if (item.route) onNavigate(item.route);
  };

  return (
    <li className={`in-app-side-entry depth-${depth}`}>
      <button
        type="button"
        className={[
          "in-app-side-item",
          item.active ? "active" : "",
          expanded ? "expanded" : "",
          item.disabled ? "disabled" : "",
        ].join(" ")}
        onClick={handleClick}
        aria-current={item.active ? "page" : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        title={item.label}
      >
        <Icon name={item.icon} className="in-app-side-icon" />
        <span className="in-app-side-label">{item.label}</span>
        {hasChildren ? <Icon name="expand_more" className="in-app-side-chevron" /> : null}
      </button>

      {hasChildren ? (
        <ul className="in-app-side-children" hidden={!expanded}>
          {visibleChildren.map((child) => (
            <Fragment key={child.id}>
              <SideNavItem item={child} depth={depth + 1} onNavigate={onNavigate} />
            </Fragment>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export type SideNavProps = {
  navItems: DashboardNavItem[];
  onNavigate: (route: string) => void;
};

export function SideNav({ navItems, onNavigate }: SideNavProps) {
  return (
    <aside className="in-app-side-nav" aria-label="App navigation">
      <nav>
        <ul className="in-app-side-list">
          {navItems.map((item) => (
            <Fragment key={item.id}>
              <SideNavItem item={item} onNavigate={onNavigate} />
            </Fragment>
          ))}
        </ul>
      </nav>
    </aside>
  );
}
